import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Sparkles, Send, Trash2 } from "lucide-react";
import AppShell, { getActiveSchemeId } from "@/components/AppShell";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { streamSSE } from "@/lib/sseStream";

const SUGGESTIONS = [
  "When is our next AGM?",
  "How are levies set under NSW strata law?",
  "What's the difference between an ordinary and special motion?",
  "Can the committee approve major renovations without an owner vote?",
];

const API = process.env.REACT_APP_BACKEND_URL;

export default function Ask() {
  const schemeId = getActiveSchemeId();
  const [q, setQ] = useState("");
  const [history, setHistory] = useState([]);    // session history (full structured) — top of feed
  const [persistedHistory, setPersistedHistory] = useState([]); // qa_threads from /history
  const [busy, setBusy] = useState(false);
  const [streaming, setStreaming] = useState(false);
  const [liveAnswer, setLiveAnswer] = useState("");
  const [currentQuestion, setCurrentQuestion] = useState("");
  const [rateLimit, setRateLimit] = useState(null);

  const loadHistory = async () => {
    try {
      const { data } = await api.get(`/schemes/${schemeId}/ai/qa/history`);
      setPersistedHistory(Array.isArray(data) ? data : []);
    } catch (_) { setPersistedHistory([]); }
  };
  useEffect(() => { if (schemeId) loadHistory(); }, [schemeId]);

  const getToken = () => localStorage.getItem("opstrai.session_token");

  const ask = async (text) => {
    const question = (text || q).trim();
    if (!question) return;
    setRateLimit(null);
    setBusy(true); setStreaming(true); setLiveAnswer("");
    setCurrentQuestion(question);

    await streamSSE({
      url: `${API}/api/schemes/${schemeId}/ai/qa`,
      body: { question },
      headers: { Authorization: `Bearer ${getToken()}` },
      onEvent: ({ event, data }) => {
        if (event === "token") {
          setBusy(false);
          setLiveAnswer((p) => p + (data?.text || ""));
        } else if (event === "done") {
          setHistory((h) => [{ question, ...data }, ...h].slice(0, 10));
          setStreaming(false); setBusy(false);
          setLiveAnswer(""); setCurrentQuestion("");
          setQ("");
          loadHistory();  // refresh persisted list
        } else if (event === "error") {
          toast.error(data?.message || "AI provider error");
          setStreaming(false); setBusy(false);
          setLiveAnswer(""); setCurrentQuestion("");
        }
      },
      onError: (err) => {
        if (err?.status === 429) {
          const secs = parseInt(err?.retryAfter || "3600", 10);
          const mins = Math.max(1, Math.ceil(secs / 60));
          setRateLimit({
            retry_min: mins,
            message: `You've hit the AI question limit (30 per hour). Try again in about ${mins} minute${mins === 1 ? "" : "s"}.`,
          });
        } else {
          toast.error(err?.message || apiError(err) || "Failed to ask");
        }
        setStreaming(false); setBusy(false);
        setLiveAnswer(""); setCurrentQuestion("");
      },
    });
  };

  const removePersisted = async (thread_id) => {
    try {
      await api.delete(`/schemes/${schemeId}/ai/qa/history/${thread_id}`);
      setPersistedHistory((p) => p.filter((t) => t.thread_id !== thread_id));
    } catch (e) { toast.error(apiError(e)); }
  };

  const replayQuestion = (question) => { setQ(question); window.scrollTo({ top: 0, behavior: "smooth" }); };

  return (
    <AppShell>
      <div className="max-w-3xl mx-auto px-5 lg:px-8 py-8 lg:py-10" data-testid="ask-page">
        <span className="inline-flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.18em] mb-2" style={{ color: "var(--blue-dark)" }}>
          <Sparkles size={12}/> Ask
        </span>
        <h1 className="text-2xl font-bold" style={{ color: "var(--navy)" }}>Ask a question about your scheme</h1>
        <p className="text-sm mt-1 mb-6" style={{ color: "var(--slate)" }}>
          Plain-English answers grounded in your scheme's state and recent activity. Not legal advice — always confirm with the committee or a licensed professional.
        </p>

        {/* Recent questions (qa_threads persistence) */}
        <div className="mb-6" data-testid="ask-recent-history">
          <div className="text-[10px] font-bold uppercase tracking-wider mb-2" style={{ color: "var(--slate)" }}>Recent questions</div>
          {persistedHistory.length === 0 ? (
            <p className="text-xs italic" style={{ color: "var(--tagline-grey)" }} data-testid="ask-recent-empty">Your recent questions will appear here.</p>
          ) : (
            <div className="space-y-2">
              {persistedHistory.map((t) => (
                <div key={t.thread_id} className="rounded-md border p-3 text-xs" style={{ borderColor: "var(--border)", background: "var(--white)" }} data-testid={`ask-history-row-${t.thread_id}`}>
                  <div className="flex items-start justify-between gap-3">
                    <button onClick={() => replayQuestion(t.question)} className="text-left flex-1 font-semibold underline" style={{ color: "var(--navy)" }} data-testid={`ask-history-question-${t.thread_id}`}>
                      {t.question}
                    </button>
                    <button onClick={() => removePersisted(t.thread_id)} aria-label="delete" data-testid={`ask-history-delete-${t.thread_id}`}>
                      <Trash2 size={13} style={{ color: "var(--alert)" }}/>
                    </button>
                  </div>
                  <p className="mt-1 line-clamp-2" style={{ color: "var(--slate)" }} data-testid={`ask-history-answer-${t.thread_id}`}>
                    {(t.answer || "").slice(0, 120)}{(t.answer || "").length > 120 ? "…" : ""}
                  </p>
                  {t.context_used?.hint && (
                    <p className="mt-1 text-[10px] italic" style={{ color: "var(--tagline-grey)" }} data-testid={`ask-history-hint-${t.thread_id}`}>
                      Asked when {t.context_used.hint}
                    </p>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        <form onSubmit={(e) => { e.preventDefault(); ask(); }} className="space-y-3 mb-6" data-testid="ask-form">
          <Textarea rows={3} value={q} onChange={(e) => setQ(e.target.value)} placeholder="e.g. Can the committee approve major works without a vote?" data-testid="ask-input"/>
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="flex flex-wrap gap-1.5">
              {SUGGESTIONS.map((s, i) => (
                <button key={i} type="button" onClick={() => { setQ(s); ask(s); }}
                  className="text-xs px-2.5 py-1 rounded-full border hover:bg-slate-50"
                  style={{ borderColor: "var(--border)", color: "var(--slate)" }}
                  data-testid={`ask-suggest-${i}`}>{s}</button>
              ))}
            </div>
            <Button type="submit" disabled={busy || !q.trim()} style={{ background: "var(--blue)", color: "white" }} data-testid="ask-submit">
              {busy ? <>Thinking…</> : <><Send size={14} className="mr-1.5"/> Ask</>}
            </Button>
          </div>
        </form>

        {rateLimit && (
          <div className="rounded-2xl border p-4 mb-4 flex items-start gap-3" style={{ borderColor: "var(--warning)", background: "var(--warning-light)" }} data-testid="ask-rate-limit-card">
            <Sparkles size={16} style={{ color: "var(--warning)", flexShrink: 0, marginTop: 2 }}/>
            <div className="flex-1">
              <div className="text-sm font-semibold" style={{ color: "var(--warning)" }}>AI question limit reached</div>
              <p className="text-xs mt-1" style={{ color: "var(--navy)" }}>{rateLimit.message}</p>
            </div>
          </div>
        )}

        {streaming && liveAnswer && (
          <div className="rounded-2xl border bg-white p-5 mb-4" style={{ borderColor: "var(--blue)" }} data-testid="ask-streaming">
            <div className="flex items-center gap-2 mb-2 text-[11px] font-semibold uppercase tracking-wider" style={{ color: "var(--blue)" }}>
              <span className="ai-dot" data-testid="ask-streaming-indicator"/> Streaming…
            </div>
            <div className="text-xs font-semibold uppercase tracking-wider mb-2" style={{ color: "var(--blue-dark)" }}>Your question</div>
            <p className="text-sm font-medium mb-3" style={{ color: "var(--navy)" }}>{currentQuestion}</p>
            <pre className="whitespace-pre-wrap text-sm font-sans" style={{ color: "var(--navy)" }} data-testid="ask-live-text">{liveAnswer}</pre>
          </div>
        )}

        {history.length > 0 && (
          <div className="space-y-4" data-testid="ask-history">
            {history.map((h, i) => (
              <div key={i} className="rounded-2xl border bg-white p-5" style={{ borderColor: "var(--border)" }} data-testid={`ask-item-${i}`}>
                <div className="text-xs font-semibold uppercase tracking-wider mb-2" style={{ color: "var(--blue-dark)" }}>Your question</div>
                <p className="text-sm font-medium mb-4" style={{ color: "var(--navy)" }}>{h.question}</p>
                <div className="text-xs font-semibold uppercase tracking-wider mb-2" style={{ color: "var(--blue-dark)" }}>Answer</div>
                <div className="text-sm whitespace-pre-wrap" style={{ color: "var(--navy)" }} data-testid={`ask-answer-${i}`}>{h.answer}</div>
                <div className="mt-4 pt-3 border-t flex items-center justify-between text-[10px]" style={{ borderColor: "var(--border)", color: "var(--tagline-grey)" }}>
                  <span>{h.model}</span>
                  <span>{h.context_used?.state} · {h.context_used?.hint}</span>
                </div>
                <p className="text-[11px] mt-3 italic" style={{ color: "var(--tagline-grey)" }}>{h.disclaimer}</p>
              </div>
            ))}
          </div>
        )}
      </div>
    </AppShell>
  );
}
